/**
 * Where a field is used decides who answers it (owner, 2026-10-01):
 *
 *   used only inside seller / buyer / property blocks (or rows repeated for
 *   each, or a list of names)            -> asked for EACH seller / buyer / property
 *   used only outside them               -> asked ONCE for the document
 *   used in both places, or not at all   -> left as the admin set it
 *
 * The admin builds the form by where he puts the fields; he never has to set
 * "for each seller" by hand, and a seller's details can never end up asked
 * once for all sellers. The editor applies this as he types, and the Worker
 * applies it again when a template is created or saved.
 */
import type { Condition, ContentBlock, Expr, FieldDef, Sentence, Template } from '@saledeed/shared';

export interface FieldUse {
  /** Uses inside a block / repeated row / list of names. */
  inside: number;
  /** Uses anywhere else (normal text, tables, clauses, conditions outside blocks). */
  outside: number;
}

export function fieldUses(template: Template): Map<string, FieldUse> {
  const uses = new Map<string, FieldUse>();
  const note = (field: string, inside: boolean) => {
    const u = uses.get(field) ?? { inside: 0, outside: 0 };
    if (inside) u.inside++; else u.outside++;
    uses.set(field, u);
  };
  const cond = (c: Condition | undefined, inside: boolean) => { if (c) note(c.field, inside); };
  const sentence = (s: Sentence, inside: boolean) => {
    cond(s.when, inside);
    for (const run of s.runs) {
      const runs = run.kind === 'fragment' ? run.runs : [run];
      if (run.kind === 'fragment') cond(run.when, inside);
      for (const r of runs) {
        if (r.kind === 'placeholder') note(r.field, inside);
        // A list of names reads the field of every seller / buyer: always per item.
        else if (r.kind === 'partyRef') for (const f of Object.values(r.fields)) if (f) note(f, true);
      }
    }
  };
  const content = (b: ContentBlock, inside: boolean) => {
    switch (b.type) {
      case 'heading': sentence(b.sentence, inside); return;
      case 'paragraph': b.sentences.forEach(s => sentence(s, inside)); return;
      case 'list': b.items.forEach(it => it.sentences.forEach(s => sentence(s, inside))); return;
      case 'table':
        for (const row of b.rows) {
          const rowInside = inside || row.repeat !== undefined;
          row.cells.forEach(c => c.sentences.forEach(s => sentence(s, rowInside)));
        }
    }
  };
  for (const b of template.blocks) {
    if (b.type === 'partyBlock') Object.values(b.variants).forEach(v => v.forEach(cb => content(cb, true)));
    else if (b.type === 'clauseSection') b.entries.forEach(e => e.options.forEach(o => o.body.forEach(p => content(p, false))));
    else content(b, false);
  }
  // "The total of X over every property" needs X per property.
  const sums = (e: Expr): void => {
    if ('sum' in e) note(e.sum, true);
    else if ('op' in e) { sums(e.a); sums(e.b); }
    else if ('if' in e) { sums(e.if.a); sums(e.if.b); sums(e.then); sums(e.else); }
  };
  for (const f of template.fields) if (f.formula) sums(f.formula);
  return uses;
}

/** The scope the field's uses call for, or null when they do not decide it. */
export function scopeFromUse(use: FieldUse | undefined): FieldDef['scope'] | null {
  if (!use) return null;
  if (use.inside > 0 && use.outside === 0) return 'party';
  if (use.outside > 0 && use.inside === 0) return 'document';
  return null;
}

export interface ScopeChange { field: string; label: string; scope: FieldDef['scope'] }

/** The template with each field's scope following its uses, and what changed. */
export function normaliseScopes(template: Template): { template: Template; changed: ScopeChange[] } {
  const uses = fieldUses(template);
  const changed: ScopeChange[] = [];
  const fields = template.fields.map(f => {
    const scope = scopeFromUse(uses.get(f.id));
    if (!scope || scope === f.scope) return f;
    changed.push({ field: f.id, label: f.label, scope });
    return { ...f, scope };
  });
  return { template: changed.length ? { ...template, fields } : template, changed };
}
